import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { Loader2, Archive, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import StatusBadge from "@/components/shared/StatusBadge";

const YEARS = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i); // current + 2 back = up to 2 years
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function OldShipments() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("delivered");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [search, setSearch] = useState("");
  const [shipments, setShipments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchOldShipments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { archive_reason: tab, limit: 100 };
      if (year) params.year = year;
      if (year && month) params.month = month;
      if (search) params.search = search;
      const res = await api.get("/v2/old-shipments", { params });
      setShipments(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load old shipments");
    } finally {
      setLoading(false);
    }
  }, [tab, year, month, search]);

  useEffect(() => { fetchOldShipments(); }, [fetchOldShipments]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase flex items-center gap-2">
            <Archive className="w-6 h-6" /> Old Shipments
          </h1>
          <p className="text-slate-500 text-sm">
            {total} archived shipments · admin-only view, up to 2 years of history · nothing here is ever deleted
          </p>
        </div>

        <div className="flex gap-2 border-b border-slate-200">
          <button
            onClick={() => setTab("delivered")}
            data-testid="old-shipments-tab-delivered"
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "delivered" ? "border-brand-500 text-brand-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            Delivered
          </button>
          <button
            onClick={() => setTab("cancelled")}
            data-testid="old-shipments-tab-cancelled"
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "cancelled" ? "border-brand-500 text-brand-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            Cancelled
          </button>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search order ID or tracking ID..."
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500"
            />
          </div>
          <select value={year} onChange={(e) => { setYear(e.target.value); setMonth(""); }}
            className="border border-slate-200 rounded-sm text-sm px-3 py-2">
            <option value="">All Years</option>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {year && (
            <select value={month} onChange={(e) => setMonth(e.target.value)}
              className="border border-slate-200 rounded-sm text-sm px-3 py-2">
              <option value="">All Months</option>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          )}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : shipments.length === 0 ? (
            <div className="p-12 text-center">
              <Archive className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No archived {tab} shipments found for this filter.</p>
            </div>
          ) : (
            <div className="space-y-3 p-3">
              {shipments.map((s) => (
                <div key={s.id} data-testid="old-shipment-row" className="p-4 bg-white border border-slate-200 rounded-xl">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 mb-1.5">
                        <button
                          onClick={() => navigate(`/admin/shipments/${s.id}/tracking`)}
                          data-testid={`old-shipment-link-${s.id}`}
                          className="font-heading font-bold text-brand-600 text-sm hover:underline"
                        >
                          {s.order_id || s.tracking_id}
                        </button>
                        {s.service_level && (
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${
                            s.service_level === "NDD" ? "bg-red-100 text-red-700"
                            : s.service_level === "EXPRESS" ? "bg-amber-100 text-amber-700"
                            : "bg-blue-100 text-blue-700"
                          }`}>
                            {s.service_level}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div className="flex items-start gap-2">
                          <span className="text-pink-500 mt-0.5">📍</span>
                          <div>
                            <div className="text-xs font-semibold text-slate-700">{s.origin?.name || "—"}</div>
                            <div className="text-xs text-slate-500">{s.origin?.city}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-emerald-600 mt-0.5">🏁</span>
                          <div>
                            <div className="text-xs font-semibold text-slate-700">{s.destination?.name || "—"}</div>
                            <div className="text-xs text-slate-500">{s.destination?.city}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-6">
                      <div>
                        <div className="text-slate-400 uppercase text-[10px] font-bold mb-0.5">Weight</div>
                        <div className="font-semibold text-slate-700 text-sm">{s.chargeable_weight ?? s.package?.weight}kg</div>
                      </div>
                      <div>
                        <div className="text-slate-400 uppercase text-[10px] font-bold mb-0.5">Boxes</div>
                        <div className="font-semibold text-slate-700 text-sm">{s.box_count || 1}</div>
                      </div>
                      <div>
                        <div className="text-slate-400 uppercase text-[10px] font-bold mb-0.5">Price</div>
                        <div className="font-semibold text-slate-700 text-sm">₹{(s.price || 0).toFixed(2)}</div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 min-w-[110px] items-end">
                      <StatusBadge status={s.status} />
                      <div className="text-xs text-slate-400 whitespace-nowrap">
                        {tab === "delivered"
                          ? (s.delivered_at ? new Date(s.delivered_at).toLocaleDateString("en-IN") : "—")
                          : (s.cancelled_at ? new Date(s.cancelled_at).toLocaleDateString("en-IN") : "—")}
                      </div>
                      {tab === "cancelled" && s.cancelled_reason && (
                        <div className="text-[10px] text-slate-400 text-right max-w-[150px]" title={s.cancelled_reason}>
                          {s.cancelled_reason}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
